import { forwardRef } from 'react';
import { nanoid as generate } from 'nanoid';
import { Colors, Svgs } from 'environment';
import { withMemo } from 'helpers/HOCs';
import { InputType } from 'types/index';
import {
	Checkbox,
	CheckIcon,
	Container,
	Wrapper,
	HiddenInput,
	CheckboxWrapper
} from '../../Checkbox/Checkbox.style';
import { Tooltip } from '../../Tooltip';
import { Typography } from 'components/UI/Typography';
import { useMemoOnce } from 'hooks/utils';

interface Props {
	name: string;
	label: string;
	value: string;
	error?: string;
	tooltip?: string;
	checked?: boolean;
	className?: string;
	disabled?: boolean;
	insideGroup?: boolean;
	dataTestId?: string;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const Component = forwardRef<HTMLInputElement, Props>(
	(
		{
			name,
			label,
			value,
			error,
			tooltip,
			checked,
			className,
			disabled,
			insideGroup,
			dataTestId,
			onBlur,
			onChange
		},
		ref
	) => {
		const id = useMemoOnce(() => `${name}_${value}_${generate()}`);

		// TRIGGER BLUR SO FORM SENSES A CHANGE WHEN GOING FROM CHECKED TO UNCHECKED STATE
		function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
			const { checked } = e.target;

			if (!checked) e.target.blur();
		}

		function dataTestIdValue() {
			if (dataTestId) {
				return (
					dataTestId.replace(/\s/g, '').toLowerCase() +
					'_' +
					label.replace(/\s/g, '').toLowerCase() +
					'_checkbox'
				);
			}

			return label.replace(/\s/g, '').toLowerCase() + '_checkbox';
		}

		return (
			<Wrapper disabled={disabled}>
				<HiddenInput
					ref={ref}
					id={id}
					type={InputType.Checkbox}
					name={name}
					value={value}
					checked={checked}
					disabled={disabled}
					onBlur={onBlur}
					onChange={onChange ?? handleOnChange}
				/>

				{/*
					THIS 2ND HIDDEN INPUT SERVES THE PURPOSE OF
					HAVING AT ALL TIMES AN INITIAL VALUE OF STRING ARRAY,
					OTHERWISE THE CHECKBOX WILL AUTOMATICALLY CAST TO THE FIRST VALUE
					AS STRING AND DESTROY THE INIITAL VALUES OF EMPTY ARRAY GIVEN TO THE FORM
				 */}
				{insideGroup && <HiddenInput ref={ref} type={InputType.Checkbox} name={name} />}

				<label htmlFor={id}>
					<Container disabled={disabled} className={className}>
						<CheckboxWrapper>
							<Checkbox>
								<CheckIcon
									customSize={1.1}
									svg={Svgs.Checkmark}
									colors={{ color: Colors.white }}
								/>
							</Checkbox>
						</CheckboxWrapper>

						{label && (
							<Typography.Paragraph
								data-test-id={dataTestIdValue()}
								marginOffset={{ top: 0.2, left: 0.8 }}
								color={error !== undefined ? Colors.text.error : undefined}
								data-tip={tooltip}
								data-for={id}
							>
								{label}
							</Typography.Paragraph>
						)}
					</Container>
				</label>

				{tooltip !== undefined && <Tooltip id={id} />}
			</Wrapper>
		);
	}
);

export const CheckboxUncontrolled = withMemo(Component);
